import React from "react";
import { Input, Dropdown, Option } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import GenerateAudioContainer from "../../../Containers/GenericContainer/GenerateAudioContainer";
import "../../CSS/SurveyComponent.css";

export default function QuestionComponent({
  question,
  questionName,
  setCallCenterData,
  callFlowId,
  callFlowLanguage,
  audioList,
}) {
  const { t } = useTranslation();

  const surveyTypes = [
    { key: "YES_OR_NO", label: t("yesOrNo") },
    { key: "RATING", label: t("rating") },
    // NUMERICAL retiré : il était PROPOSÉ ici mais le moteur ne l'a jamais implémenté —
    // `FlowEngineUnify/src/Services/Survey.js` ne traite que YES_OR_NO et RATING, une réponse
    // NUMERICAL tombant dans « on garde la chaîne », sans échelle ni validation. Les CDR
    // historiques qui le portent restent lisibles côté analytique.
    { key: "OPEN_QUESTION", label: t("openQuestion") },
  ];

  const setField = (field, value) => {
    setCallCenterData((prev) => ({
      ...prev,
      survey: {
        ...prev.survey,
        questions: {
          ...prev.survey.questions,
          [questionName]: {
            ...prev.survey.questions[questionName],
            [field]: value,
          },
        },
      },
    }));
  };

  const setAudio = (audio) => {
    setCallCenterData((prev) => ({
      ...prev,
      survey: {
        ...prev.survey,
        questions: {
          ...prev.survey.questions,
          [questionName]: {
            ...prev.survey.questions[questionName],
            audio,
          },
        },
      },
    }));
  };

  return (
    <div className="question-fields">
      {/* Type */}
      <div className="question-row">
        <div className="question-label">{t("type")}</div>
        <Dropdown
          value={
            surveyTypes.find((t) => t.key === question?.type)?.label || ""
          }
          onOptionSelect={(e, data) => {
            const selectedType = surveyTypes.find(
              (t) => t.label === data.optionValue
            );
            if (selectedType) setField("type", selectedType.key);
          }}
        >
          {surveyTypes.map((type) => (
            <Option key={type.key} value={type.label}>
              {type.label}
            </Option>
          ))}
        </Dropdown>
      </div>

      {/* Question Text */}
      <div className="question-row">
        <div className="question-label">{t("questionText")}</div>
        <Input
          value={question?.questionText || ""}
          onChange={(e, data) => setField("questionText", data.value)}
        />
      </div>

      {/* Question Order */}
      <div className="question-row">
        <div className="question-label">{t("questionOrder")}</div>
        <Input
          type="number"
          value={question?.Order || ""}
          onChange={(e, data) => setField("Order", data.value)}
        />
      </div>

      {/* Réglages propres à la question OUVERTE.
          Les deux silences ne mesurent pas la même chose, et confondre les deux donne une
          enquête inutilisable :
            • délai avant de parler — le client n'a RIEN dit ; on abandonne la question ;
            • silence de fin        — il a parlé puis s'est tu : sa réponse est terminée.
          C'est le second qui remplace la touche du clavier. Trop court, il coupe la parole
          au milieu d'une phrase ; les valeurs par défaut du moteur sont 8 s et 2 s. */}
      {question?.type === "OPEN_QUESTION" && (
        <>
          <div className="question-row">
            <div className="question-label">{t("answerTimeout")}</div>
            <Input
              type="number"
              value={question?.answerTimeout || ""}
              placeholder="8"
              onChange={(e, data) => setField("answerTimeout", data.value)}
            />
          </div>
          <div className="question-row">
            <div className="question-label">{t("endSilence")}</div>
            <Input
              type="number"
              value={question?.endSilence || ""}
              placeholder="2"
              onChange={(e, data) => setField("endSilence", data.value)}
            />
          </div>
        </>
      )}

      {/* Audio */}
      <div className="question-row">
        <div className="question-label">{t("announcement")}</div>
        <GenerateAudioContainer
                /* La langue CHOISIE, pas celle du flux.
                   Le rappel ignorait son argument et réécrivait `callFlowLanguage` : sélectionner
                   le français dans le générateur d'audio n'avait donc aucun effet, la valeur
                   retombait sur la langue du flux à chaque enregistrement. Tous les autres
                   appelants de ce composant relaient bien la langue — les deux blocs de
                   l'enquête étaient les seuls à la perdre.

                   La conséquence dépassait l'affichage : `Survey.js` passe cette langue à la
                   reconnaissance vocale d'ACS. Une réponse française reconnue par un modèle
                   anglais ne lève aucune erreur, elle rend un texte faux. */
          setLangue={(lang) =>
            setAudio({
              ...question.audio,
              type: "text2speech",
              language: lang || callFlowLanguage,
            })
          }
          setDelete={() =>
            setAudio({
              type: "text2speech",
              audioFileUrl: "",
              text: "",
              selectedMedia: null,
            })
          }
          saveAudio={(url) =>
            setAudio({ ...question.audio, audioFileUrl: url })
          }
          setText={(text) =>
            setAudio({
              ...question.audio,
              text,
              audioFileUrl: "",
              type: "text2speech",
            })
          }
          setAudio={(selectedMedia) =>
            setAudio({
              ...question.audio,
              selectedMedia,
              audioFileUrl: selectedMedia?.url || "",
              text: "",
              type: "Audiofile",
            })
          }
          setType={(type) => setAudio({ ...question.audio, type })}
          audioFileUrl={question?.audio?.audioFileUrl || ""}
          fileName={question?.audio?.selectedMedia?.header}
          type={question?.audio?.type || "text2speech"}
          text={question?.audio?.text || ""}
          path={`survey/${questionName}`}
          defaultLanguage={question?.audio?.language || callFlowLanguage}
        />
      </div>
    </div>
  );
}
